import React, { useState, useEffect, useCallback } from "react";
import { Breadcrumb, Layout, Steps, Card, Button, message, theme } from "antd";
import { useAuth } from "../hooks/useAuth";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import { useScopedNavigate } from "../components/MarketerView/useScopedNavigate";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { useGetBrandsQuery, useCreateSurveyMutation, useGetSurveyCohortsQuery, useGetSurveysQuery, useUpdateBrandMutation } from "../services/nestjs-base-api";
import { ISurveyTheme, IBrand } from "../services/nestjs-base-api/types";
import BasicInfoStep from "../components/Survey/BasicInfoStep";
import ConfigurationStep from "../components/Survey/ConfigurationStep";
import ThemeStep from "../components/Survey/ThemeStep";

const { Content } = Layout;

interface SurveyFormData {
    name: string;
    brandId: number;
    cohortId: number;
    countryCode: string;
    languageCode: string;
    description?: string;
    startsAt?: Date;
    endsAt?: Date;
    questionsPerResponse: number;
    rotationStrategy: 'sequential' | 'random' | 'balanced' | 'adaptive';
    theme: ISurveyTheme;
    showProgressBar: boolean;
    animationSpeed: number;
    allowSkip: boolean;
    allowBackNavigation: boolean;
    persistProgress: boolean;
    expirationHours: number;
    completionTitle: string;
    completionSubtitle: string;
    completionButtonText: string;
    completionButtonLink?: string;
    showCompletionButton: boolean;
    completionSecondaryButtonText?: string;
    completionSecondaryButtonLink?: string;
    showReward?: boolean;
    discountCode?: string;
    offerDescription?: string;
    offerTerms?: string;
    showCompletionSecondaryButton: boolean;
    collectEmail: boolean;
    emailRequired: boolean;
    emailPlaceholder: string;
    npsRequired: boolean;
    showWelcomePage: boolean;
    finalQuestionId?: number;
}

const CreateSurveyPage: React.FunctionComponent = () => {
    const { user } = useAuth();
    const intl = useIntl();
    const navigate = useScopedNavigate();
    const location = useLocation();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const topOfWizard = 170;

    // Check for prefilled data from navigation state
    const prefillData = location.state?.prefillData;

    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState<SurveyFormData>({
        name: prefillData?.name || '',
        brandId: prefillData?.brandId || 0,
        cohortId: prefillData?.cohortId || 0,
        countryCode: prefillData?.countryCode || 'AU',
        languageCode: prefillData?.languageCode || 'en',
        description: prefillData?.description || '',
        questionsPerResponse: prefillData?.questionsPerResponse || 12,
        rotationStrategy: prefillData?.rotationStrategy || 'balanced',
        theme: prefillData?.theme || {
            primaryColor: '#4338ca',
            secondaryColor: '#1e40af',
            backgroundColor: '#1e293b',
            cardColor: '#334155',
            accentColor: '#3b82f6'
        },
        showProgressBar: prefillData?.showProgressBar ?? true,
        animationSpeed: prefillData?.animationSpeed || 800,
        allowSkip: prefillData?.allowSkip ?? false,
        allowBackNavigation: prefillData?.allowBackNavigation ?? true,
        persistProgress: prefillData?.persistProgress ?? true,
        expirationHours: prefillData?.expirationHours || 24,
        completionTitle: prefillData?.completionTitle || 'Survey Complete!',
        completionSubtitle: prefillData?.completionSubtitle || 'Thank you for taking the time to share your feedback...',
        completionButtonText: prefillData?.completionButtonText || 'Visit our website',
        completionButtonLink: prefillData?.completionButtonLink || '',
        showCompletionButton: prefillData?.showCompletionButton ?? false,
        completionSecondaryButtonText: prefillData?.completionSecondaryButtonText || 'Leave us a review',
        completionSecondaryButtonLink: prefillData?.completionSecondaryButtonLink || '',
        showReward: Boolean(prefillData?.discountCode),
        discountCode: prefillData?.discountCode || '',
        offerDescription: prefillData?.offerDescription || '',
        offerTerms: prefillData?.offerTerms || '',
        showCompletionSecondaryButton: prefillData?.showCompletionSecondaryButton ?? false,
        collectEmail: prefillData?.collectEmail ?? true,
        emailRequired: prefillData?.emailRequired ?? true,
        emailPlaceholder: prefillData?.emailPlaceholder || 'Enter your email address',
        npsRequired: prefillData?.npsRequired ?? true,
        showWelcomePage: prefillData?.showWelcomePage ?? true,
        finalQuestionId: prefillData?.finalQuestionId
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    // Fetch brands for dropdown
    const { data: brands, isLoading: brandsLoading } = useGetBrandsQuery({
        jwtToken: user?.jwtToken!,
        organisationId: user?.organisation?.id!
    }, {
        skip: !user?.jwtToken || !user?.organisation?.id
    });

    // Fetch cohorts for dropdown
    const { data: cohorts, isLoading: cohortsLoading, refetch: refetchCohorts } = useGetSurveyCohortsQuery({
        jwtToken: user?.jwtToken!,
        organisationId: user?.organisation?.id!
    }, {
        skip: !user?.jwtToken || !user?.organisation?.id
    });

    // Fetch all surveys for the organization
    const { data: allSurveys = [], isLoading: surveysLoading, isError: surveysError } = useGetSurveysQuery({
        jwtToken: user?.jwtToken!,
        organisationId: user?.organisation?.id!
    }, {
        skip: !user?.jwtToken || !user?.organisation?.id
    });

    // Filter cohorts to exclude those with active surveys for the selected brand + country combination
    const availableCohorts = React.useMemo(() => {
        if (!cohorts) return [];
        if (!formData.brandId) return [];
        // Don't show cohorts while loading OR if there's an error loading surveys
        if (surveysLoading || surveysError) return [];

        // Find cohort IDs that already have active surveys for this brand + country combination
        // We need to track brand + country + cohort as a unique combination
        const activeSurveyKeys = new Set(
            allSurveys
                .filter(survey => {
                    // Check both brandId and brand.id (API might return it nested)
                    const surveyBrandId = survey.brandId || survey.brand?.id;
                    const surveyCountryCode = survey.countryCode || 'AU';
                    const selectedCountryCode = formData.countryCode || 'AU';

                    // Only match surveys with same brand AND same country
                    return surveyBrandId === formData.brandId
                        && surveyCountryCode === selectedCountryCode
                        && survey.status === 'active';
                })
                .map(survey => {
                    // Check both cohortId and cohort.id (API might return it nested)
                    return survey.cohortId || survey.cohort?.id;
                })
                .filter(id => id !== undefined) // Remove any undefined values
        );

        // Filter out cohorts that already have active surveys for this brand + country
        return cohorts.filter(cohort => !activeSurveyKeys.has(cohort.id));
    }, [cohorts, allSurveys, formData.brandId, formData.countryCode, surveysLoading, surveysError]);

    const [createSurvey, { isLoading: isSubmitting }] = useCreateSurveyMutation();
    const [updateBrand] = useUpdateBrandMutation();

    // Clear localStorage draft on mount to start fresh
    useEffect(() => {
        localStorage.removeItem('survey-draft');
    }, []);

    useEffect(() => {
        localStorage.setItem('survey-draft', JSON.stringify(formData));
    }, [formData]);

    const handleBrandTemplateTypeUpdate = useCallback(async (brandId: number, templateType: string) => {
        try {
            await updateBrand({
                jwtToken: user?.jwtToken!,
                organisationId: user?.organisation?.id!,
                brandId,
                payload: { templateType }
            }).unwrap();

            message.success(`Product type updated to ${templateType === 'durable' ? 'Durable goods' : 'Perishable goods'}`);
        } catch (error) {
            console.error('Failed to update brand template type:', error);
            message.error('Failed to update product type');
        }
    }, [updateBrand, user?.jwtToken, user?.organisation?.id]);

    // Auto-select brand if there's only one
    useEffect(() => {
        if (brands && brands.length === 1 && formData.brandId !== brands[0].id) {
            setFormData(prev => ({
                ...prev,
                brandId: brands[0].id
            }));
        }
    }, [brands, formData.brandId]);

    // Update theme colors when brand is selected (only if not duplicating a survey with existing theme)
    useEffect(() => {
        if (formData.brandId && brands) {
            const selectedBrand = brands.find((brand: IBrand) => brand.id === formData.brandId);
            if (selectedBrand) {
                // Only update primaryColor from brand if we don't have prefilled theme data
                if (!prefillData?.theme) {
                    setFormData(prev => ({
                        ...prev,
                        theme: {
                            ...prev.theme,
                            primaryColor: selectedBrand.primaryColor || '#4338ca'
                        }
                    }));
                }

                // If we have prefillData with a templateType and the brand doesn't have one, update it
                if (prefillData?.templateType && !selectedBrand.templateType) {
                    handleBrandTemplateTypeUpdate(selectedBrand.id, prefillData.templateType);
                }
            }
        }
    }, [formData.brandId, brands, handleBrandTemplateTypeUpdate, prefillData]);

    const steps = [
        {
            title: intl.formatMessage({ id: 'survey.create.step1.title' }),
            description: intl.formatMessage({ id: 'survey.create.step1.description' })
        },
        {
            title: intl.formatMessage({ id: 'survey.create.step2.title' }),
            description: intl.formatMessage({ id: 'survey.create.step2.description' })
        },
        {
            title: intl.formatMessage({ id: 'survey.create.step3.title' }),
            description: intl.formatMessage({ id: 'survey.create.step3.description' })
        }
    ];

    const validateStep = (step: number): boolean => {
        const newErrors: Record<string, string> = {};

        if (step === 0) {
            if (!formData.name || formData.name.length < 3) {
                newErrors.name = intl.formatMessage({ id: 'survey.form.validation.nameRequired' });
            }
            if (!formData.brandId) {
                newErrors.brandId = intl.formatMessage({ id: 'survey.form.validation.brandRequired' });
            }
            if (!formData.cohortId) {
                newErrors.cohortId = 'Please select a customer cohort';
            }

            // Check if selected brand has template type
            if (formData.brandId && brands) {
                const selectedBrand = brands.find((brand: IBrand) => brand.id === formData.brandId);
                if (selectedBrand && !selectedBrand.templateType) {
                    newErrors.brandId = 'Please select the product type for this brand above';
                }
            }
        }

        if (step === 1) {
            if (formData.startsAt && formData.endsAt && formData.startsAt >= formData.endsAt) {
                newErrors.endsAt = intl.formatMessage({ id: 'survey.form.validation.endDateAfterStart' });
            }
            if (formData.questionsPerResponse < 5 || formData.questionsPerResponse > 15) {
                newErrors.questionsPerResponse = intl.formatMessage({ id: 'survey.form.validation.questionsRange' });
            }
            if (formData.animationSpeed < 200 || formData.animationSpeed > 2000) {
                newErrors.animationSpeed = intl.formatMessage({ id: 'survey.form.validation.animationSpeedRange' });
            }
            if (formData.expirationHours < 1 || formData.expirationHours > 168) {
                newErrors.expirationHours = intl.formatMessage({ id: 'survey.form.validation.expirationHoursRange' });
            }
            // Primary CTA: both fields required when shown
            if (formData.showCompletionButton) {
                if (!formData.completionButtonText?.trim()) {
                    newErrors.completionButtonText = intl.formatMessage({ id: 'survey.form.validation.buttonTextRequired' });
                }
                if (!formData.completionButtonLink?.trim()) {
                    newErrors.completionButtonLink = intl.formatMessage({ id: 'survey.form.validation.buttonLinkRequired' });
                }
            }
            if (formData.completionButtonLink && formData.completionButtonLink.trim()) {
                try {
                    new URL(formData.completionButtonLink);
                } catch {
                    newErrors.completionButtonLink = intl.formatMessage({ id: 'survey.form.validation.invalidUrl' });
                }
            }
            // Secondary CTA (SYR-93): both fields required when shown, link must be https
            if (formData.showCompletionSecondaryButton) {
                if (!formData.completionSecondaryButtonText?.trim()) {
                    newErrors.completionSecondaryButtonText = intl.formatMessage({ id: 'survey.form.validation.secondaryButtonTextRequired' });
                }
                if (!formData.completionSecondaryButtonLink?.trim()) {
                    newErrors.completionSecondaryButtonLink = intl.formatMessage({ id: 'survey.form.validation.secondaryButtonLinkRequired' });
                }
            }
            if (formData.completionSecondaryButtonLink && formData.completionSecondaryButtonLink.trim()) {
                let secondaryLinkValid = false;
                try {
                    secondaryLinkValid = new URL(formData.completionSecondaryButtonLink).protocol === 'https:';
                } catch {
                    secondaryLinkValid = false;
                }
                if (!secondaryLinkValid) {
                    newErrors.completionSecondaryButtonLink = intl.formatMessage({ id: 'survey.form.validation.httpsUrlRequired' });
                }
            }
            if (formData.emailPlaceholder && formData.emailPlaceholder.length > 255) {
                newErrors.emailPlaceholder = intl.formatMessage({ id: 'survey.form.validation.emailPlaceholderLength' });
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Smooth-scroll to the first invalid field so a blocked Next is never silent.
    // Deferred so the validation re-render has painted the error state first.
    const scrollToFirstError = () => {
        setTimeout(() => {
            const el = document.querySelector(
                '.ant-form-item-has-error, .ant-input-status-error, .ant-input-number-status-error, .ant-picker-status-error, .ant-select-status-error'
            );
            el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    };

    const handleNext = () => {
        if (validateStep(currentStep)) {
            setCurrentStep(currentStep + 1);
            // Scroll to top to show progress indicator
            window.scrollTo({ top: topOfWizard, behavior: 'smooth' });
        } else {
            scrollToFirstError();
        }
    };

    const handlePrevious = () => {
        setCurrentStep(currentStep - 1);
        // Scroll to top to show progress indicator
        window.scrollTo({ top: topOfWizard, behavior: 'smooth' });
    };

    const handleSubmit = async () => {
        if (!validateStep(currentStep)) {
            scrollToFirstError();
            return;
        }

        try {
            const payload = {
                name: formData.name,
                brandId: formData.brandId,
                cohortId: formData.cohortId,
                countryCode: formData.countryCode || 'AU',
                languageCode: formData.languageCode || 'en',
                description: formData.description || undefined,
                status: 'active' as const,
                startsAt: formData.startsAt ? formData.startsAt.toISOString() : undefined,
                endsAt: formData.endsAt ? formData.endsAt.toISOString() : undefined,
                questionsPerResponse: formData.questionsPerResponse,
                rotationStrategy: formData.rotationStrategy,
                theme: formData.theme,
                showProgressBar: formData.showProgressBar,
                animationSpeed: formData.animationSpeed,
                allowSkip: formData.allowSkip,
                allowBackNavigation: formData.allowBackNavigation,
                persistProgress: formData.persistProgress,
                expirationHours: formData.expirationHours,
                completionTitle: formData.completionTitle,
                completionSubtitle: formData.completionSubtitle,
                completionButtonText: formData.completionButtonText,
                completionButtonLink: formData.completionButtonLink || undefined,
                showCompletionButton: formData.showCompletionButton,
                completionSecondaryButtonText: formData.completionSecondaryButtonText || undefined,
                completionSecondaryButtonLink: formData.completionSecondaryButtonLink || undefined,
                showReward: formData.showReward ?? false,
                discountCode: formData.discountCode || null,
                offerDescription: formData.offerDescription || null,
                offerTerms: formData.offerTerms || null,
                showCompletionSecondaryButton: formData.showCompletionSecondaryButton,
                collectEmail: true,
                emailRequired: true,
                emailPlaceholder: formData.emailPlaceholder,
                npsRequired: formData.npsRequired,
                showWelcomePage: formData.showWelcomePage,
                finalQuestionId: formData.finalQuestionId
            };

            const result = await createSurvey({
                jwtToken: user?.jwtToken!,
                organisationId: user?.organisation?.id!,
                payload
            }).unwrap();

            message.success(intl.formatMessage({ id: 'survey.create.success' }, { name: formData.name }));
            localStorage.removeItem('survey-draft');
            navigate(`/dashboard/surveys/${result.hash}`);
        } catch (error: any) {
            console.error('Failed to create survey:', error);
            // Show backend error message if available (e.g., cohort already has active survey)
            const errorMessage = error?.data?.message || intl.formatMessage({ id: 'survey.create.error' });
            message.error(errorMessage);
        }
    };

    const updateFormData = (updates: Partial<SurveyFormData>) => {
        setFormData(prev => ({ ...prev, ...updates }));
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <BasicInfoStep
                        formData={formData}
                        brands={brands || []}
                        brandsLoading={brandsLoading}
                        cohorts={availableCohorts}
                        cohortsLoading={cohortsLoading}
                        errors={errors}
                        onChange={updateFormData}
                        onBrandTemplateTypeUpdate={handleBrandTemplateTypeUpdate}
                        onCohortCreated={refetchCohorts}
                        hideProductTypeSelection={!!prefillData?.templateType}
                        hideBrandSelector={brands?.length === 1}
                    />
                );
            case 1:
                return (
                    <ConfigurationStep
                        formData={formData}
                        errors={errors}
                        onChange={updateFormData}
                    />
                );
            case 2:
                return (
                    <ThemeStep
                        formData={formData}
                        brands={brands || []}
                        errors={errors}
                        onChange={updateFormData}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Content style={{ margin: '0 16px' }}>
            <Breadcrumb 
                style={{ margin: '16px 0' }}
                items={[
                    { title: user?.organisation?.name || intl.formatMessage({ id: 'common.platformName' }) },
                    { title: intl.formatMessage({ id: 'section.surveys' }) },
                    { title: intl.formatMessage({ id: 'survey.create.title' }) }
                ]}
            />

            <div
                style={{
                    padding: 24,
                    minHeight: '82vh',
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <div>
                        <h2>{intl.formatMessage({ id: 'survey.create.title' })}</h2>
                        <p style={{ color: '#6b7280', margin: 0 }}>
                            {intl.formatMessage({ id: 'survey.create.subtitle' })}
                        </p>
                    </div>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/dashboard/surveys')}
                    >
                        {intl.formatMessage({ id: 'common.cancelButton' })}
                    </Button>
                </div>

                <Card>
                    <Steps current={currentStep} items={steps} style={{ marginBottom: 32 }} />
                    
                    <div style={{ minHeight: '400px' }}>
                        {renderStepContent()}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32 }}>
                        <div>
                            {currentStep > 0 && (
                                <Button onClick={handlePrevious}>
                                    {intl.formatMessage({ id: 'common.previousButton' })}
                                </Button>
                            )}
                        </div>
                        <div>
                            {currentStep < steps.length - 1 ? (
                                <Button type="primary" onClick={handleNext}>
                                    {intl.formatMessage({ id: 'common.nextButton' })}
                                </Button>
                            ) : (
                                <Button 
                                    type="primary" 
                                    icon={<SaveOutlined />}
                                    loading={isSubmitting}
                                    onClick={handleSubmit}
                                >
                                    {intl.formatMessage({ id: 'survey.create.submit' })}
                                </Button>
                            )}
                        </div>
                    </div>
                </Card>
            </div>
        </Content>
    );
};

export default CreateSurveyPage;